import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { 
  Heart, 
  Home, 
  Users, 
  MessageCircle, 
  Sparkles, 
  BookOpen, 
  User, 
  LogOut,
  Menu,
  Leaf
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Home', icon: Home },
  { path: '/discover', label: 'Discover', icon: Users },
  { path: '/matches', label: 'Matches', icon: Heart },
  { path: '/quizzes', label: 'Quizzes', icon: Sparkles },
  { path: '/education', label: 'Learn', icon: BookOpen },
];

export const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const NavLink = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    
    return (
      <button
        onClick={() => {
          navigate(item.path);
          if (mobile) setMobileMenuOpen(false);
        }}
        className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
          isActive 
            ? 'bg-primary/10 text-primary' 
            : 'text-muted-foreground hover:text-foreground hover:bg-accent'
        } ${mobile ? 'w-full justify-start' : ''}`}
        data-testid={`nav-${item.label.toLowerCase()}`}
      >
        <Icon className="w-5 h-5" strokeWidth={1.5} />
        <span className={mobile ? '' : 'hidden lg:inline'}>{item.label}</span>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-lg">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div 
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/dashboard')}
              data-testid="nav-logo"
            >
              <div className="w-9 h-9 rounded-full gradient-primary flex items-center justify-center">
                <Leaf className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
              <span className="text-xl font-serif font-semibold hidden sm:inline">Twig</span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink key={item.path} item={item} />
              ))}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-2">
              {/* Messages indicator */}
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full relative"
                onClick={() => navigate('/matches')}
                data-testid="nav-messages"
              >
                <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
              </Button>

              {/* Profile Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-full p-1" data-testid="user-menu-trigger">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user?.profile_photo_url} />
                      <AvatarFallback className="bg-primary/10 text-primary text-sm">
                        {user?.display_name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 rounded-xl">
                  <DropdownMenuItem 
                    onClick={() => navigate('/profile')}
                    className="cursor-pointer"
                    data-testid="menu-profile"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="cursor-pointer text-destructive"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Menu */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden rounded-full" data-testid="mobile-menu-trigger">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-64">
                  <div className="flex flex-col gap-2 mt-8">
                    {navItems.map((item) => (
                      <NavLink key={item.path} item={item} mobile />
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container-app py-8">
        {children}
      </main>
    </div>
  );
};

export default Layout;
